import { Analytics } from '@cobliteam/analytics';
import type { LocalUserPreferences } from '@cobliteam/api-lib/src/account/user/Types';
import { useLocalStorage } from '@cobliteam/hooks';
import type { TFunction } from '@cobliteam/i18n';
import { useTranslation } from '@cobliteam/i18n/src/hooks/useTranslation';
import type { Permission } from '@cobliteam/permissions';
import { usePermissions } from '@cobliteam/permissions';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import ProgressBar from '../../../ProgressBar';
import CategoryAccordion from './CategoryAccordion';
import type { OnboardingSteps } from './OnboardingSteps';
import { OnboardingStepsList } from './OnboardingSteps';
import StepCard from './StepCard';

type Category = (typeof OnboardingStepsList)[number];
type Step = Category['steps'][number];

interface OnboardingChecklistContentProps {
  initialCheckedSteps: OnboardingSteps[];
  onFinishOnboarding: () => void;
}

interface FinishedContentProps {
  t: TFunction;
  onGoBackToTour: () => void;
  onFinishOnboarding: () => void;
}

interface HeaderProps {
  t: TFunction;
  totalSteps: number;
  progressPercentage: number;
  progressLabel: string;
}

interface StepCardWrapperProps {
  step: Step;
  t: TFunction;
  checkedStepsMap: Map<OnboardingSteps, boolean>;
  onCheckboxChange: (step: OnboardingSteps, checked: boolean) => void;
}

interface CategoryAccordionWrapperProps {
  category: Category;
  t: TFunction;
  isAccessAllowed: (permissions: Permission[]) => boolean;
  categoryTotalStepsMap: Map<string, number>;
  categoryCheckedStepsMap: Map<string, number>;
  checkedStepsMap: Map<OnboardingSteps, boolean>;
  onCheckboxChange: (step: OnboardingSteps, checked: boolean) => void;
}

const FinishedContent = memo(({ t, onGoBackToTour, onFinishOnboarding }: FinishedContentProps) => {
  const [, setPreferences] = useLocalStorage<LocalUserPreferences>('localUserPreferences');

  const handleFinishOnboarding = useCallback(() => {
    setPreferences((prev) => ({
      ...prev,
      onboardingCompleted: true,
    }));
    onFinishOnboarding();
  }, [setPreferences, onFinishOnboarding]);

  return (
    <div className='mx-auto flex flex-col items-center justify-center gap-2 py-8'>
      <div className='rounded-full bg-primary-4 p-4'>
        <Icon name='party-popper' className='text-primary-8' size='2xl' />
      </div>
      <div className='flex max-w-md flex-col items-center justify-center gap-2'>
        <Typography variant='text-lg-semibold' color='secondary'>
          {t('onboarding_checklist.finished.title')}
        </Typography>
        <Typography variant='text-md' color='secondary' className='text-center'>
          {t('onboarding_checklist.finished.description')}
        </Typography>
      </div>
      <div className='flex flex-row gap-2'>
        <Button variant='ghost' onClick={onGoBackToTour}>
          {t('onboarding_checklist.finished.go_back_to_tour')}
        </Button>
        <Button variant='contained' onClick={handleFinishOnboarding}>
          {t('onboarding_checklist.finished.finished_button')}
        </Button>
      </div>
    </div>
  );
});

FinishedContent.displayName = 'FinishedContent';

const Header = memo(({ t, totalSteps, progressPercentage, progressLabel }: HeaderProps) => {
  return (
    <div className='-mx-4 flex flex-shrink-0 flex-col gap-1 border-gray-6 border-b px-8 pb-4'>
      <Typography variant='text-lg-semibold'>{t('onboarding_checklist.popover.title')}</Typography>
      <Typography variant='text-md' color='secondary' className='mb-4'>
        {t('onboarding_checklist.popover.subtitle_1')}
        <br />
        {t('onboarding_checklist.popover.subtitle_2')}
      </Typography>
      <ProgressBar value={totalSteps} progress={progressPercentage} label={progressLabel} />
    </div>
  );
});

Header.displayName = 'Header';

const StepCardWrapper = memo(({ step, t, checkedStepsMap, onCheckboxChange }: StepCardWrapperProps) => {
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onCheckboxChange(step.step, checked);
    },
    [step.step, onCheckboxChange]
  );

  return (
    <StepCard
      title={t(`onboarding_checklist.steps.${step.i18nKey}.title`)}
      description={t(`onboarding_checklist.steps.${step.i18nKey}.description`)}
      url={step.url}
      step={step.step}
      checked={checkedStepsMap.get(step.step) ?? false}
      onCheckboxChange={handleCheckboxChange}
      stepId={step.id}
    />
  );
});

StepCardWrapper.displayName = 'StepCardWrapper';

const CategoryAccordionWrapper = memo(
  ({ category, t, isAccessAllowed, categoryTotalStepsMap, categoryCheckedStepsMap, checkedStepsMap, onCheckboxChange }: CategoryAccordionWrapperProps) => {
    const allowedStepsInCategory = useMemo(() => category.steps.filter((step) => isAccessAllowed(step.permissions as Permission[])), [category.steps, isAccessAllowed]);

    return (
      <CategoryAccordion
        key={category.i18nKey}
        title={t(`onboarding_checklist.categories.${category.i18nKey}.title`)}
        description={t(`onboarding_checklist.categories.${category.i18nKey}.description`)}
        totalSteps={categoryTotalStepsMap.get(category.i18nKey) ?? 0}
        checkedSteps={categoryCheckedStepsMap.get(category.i18nKey) ?? 0}
      >
        {allowedStepsInCategory.map((step) => (
          <StepCardWrapper key={step.i18nKey} step={step} t={t} checkedStepsMap={checkedStepsMap} onCheckboxChange={onCheckboxChange} />
        ))}
      </CategoryAccordion>
    );
  }
);

CategoryAccordionWrapper.displayName = 'CategoryAccordionWrapper';

const OnboardingChecklistContent = ({ initialCheckedSteps, onFinishOnboarding }: OnboardingChecklistContentProps) => {
  const { t } = useTranslation('common');
  const { isAccessAllowed } = usePermissions();

  const [checkedSteps, setCheckedSteps] = useState<OnboardingSteps[]>([]);
  const [isFinished, setIsFinished] = useState(false);

  const allowedSteps = useMemo(() => {
    const allowed: OnboardingSteps[] = [];
    for (const category of OnboardingStepsList) {
      for (const step of category.steps) {
        if (isAccessAllowed(step.permissions as Permission[])) {
          allowed.push(step.step);
        }
      }
    }
    return allowed;
  }, [isAccessAllowed]);

  const totalSteps = useMemo(() => allowedSteps.length, [allowedSteps]);

  const progressPercentage = useMemo(() => {
    if (totalSteps === 0) return 0;
    return (checkedSteps.length / totalSteps) * 100;
  }, [checkedSteps.length, totalSteps]);

  const progressLabel = useMemo(() => `${checkedSteps.length}/${totalSteps}`, [checkedSteps.length, totalSteps]);

  const checkedStepsMap = useMemo(() => {
    const map = new Map<OnboardingSteps, boolean>();
    for (const step of checkedSteps) {
      map.set(step, true);
    }
    return map;
  }, [checkedSteps]);

  const categoryTotalStepsMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const category of OnboardingStepsList) {
      const totalCount = category.steps.filter((innerStep) => isAccessAllowed(innerStep.permissions as Permission[])).length;
      map.set(category.i18nKey, totalCount);
    }
    return map;
  }, [isAccessAllowed]);

  const categoryCheckedStepsMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const category of OnboardingStepsList) {
      const checkedCount = category.steps
        .filter((innerStep) => isAccessAllowed(innerStep.permissions as Permission[]))
        .filter((innerStep) => checkedStepsMap.get(innerStep.step)).length;
      map.set(category.i18nKey, checkedCount);
    }
    return map;
  }, [checkedStepsMap, isAccessAllowed]);

  const allowedCategories = useMemo(() => {
    return OnboardingStepsList.filter((category) => {
      const totalStepsInCategory = categoryTotalStepsMap.get(category.i18nKey) ?? 0;
      return totalStepsInCategory > 0;
    });
  }, [categoryTotalStepsMap]);

  const handleCheckboxChange = useCallback(
    (step: OnboardingSteps, checked: boolean) => {
      Analytics.track('Onboarding Checklist - Step checkbox changed', { step: step, checked: checked });
      setCheckedSteps((prev) => {
        if (checked) {
          if (totalSteps === prev.length + 1) {
            setIsFinished(true);
          }

          return prev.includes(step) ? prev : [...prev, step];
        }
        return prev.filter((s) => s !== step);
      });
    },
    [totalSteps]
  );

  useEffect(() => {
    setCheckedSteps(initialCheckedSteps || []);
    setIsFinished(initialCheckedSteps.length === totalSteps);
  }, [initialCheckedSteps, totalSteps]);

  return (
    <>
      {/* Fixed header section */}
      <Header t={t} totalSteps={totalSteps} progressPercentage={progressPercentage} progressLabel={progressLabel} />

      {/* Finished content */}
      {isFinished && <FinishedContent t={t} onGoBackToTour={() => setIsFinished(false)} onFinishOnboarding={onFinishOnboarding} />}

      {/* Scrollable steps section */}
      <div className={`w-full flex-1 overflow-y-auto p-4 ${isFinished ? 'hidden' : ''}`}>
        <div className='flex flex-col gap-2'>
          {allowedCategories.map((category) => (
            <CategoryAccordionWrapper
              key={category.i18nKey}
              category={category}
              t={t}
              isAccessAllowed={isAccessAllowed}
              categoryTotalStepsMap={categoryTotalStepsMap}
              categoryCheckedStepsMap={categoryCheckedStepsMap}
              checkedStepsMap={checkedStepsMap}
              onCheckboxChange={handleCheckboxChange}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default OnboardingChecklistContent;
